/**
 * Your status, from the bottom of the sidebar.
 *
 * Pressing your own face used to open your profile - the one card you never
 * need, because you already know what it says. What you actually do several
 * times an evening is say whether people should bother you.
 *
 * So the row opens this instead, and it is not a window: it grows out of the
 * line above your name, inside the sidebar, and pushes the rest of it up.
 * It opens small - the state you are in, and the line under your name - and
 * only shows the other states when you press the one you are in. Choosing
 * one folds it back, because that is the end of the errand.
 *
 * Pressing your face again, or anywhere else, puts the whole thing away.
 */

import { useEffect, useRef, useState } from 'react';
import { useSession } from '@/stores/session';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { Icon } from '@/icons/Icon';
import { EmojiField } from './EmojiField';
import { EmojiText } from './EmojiText';
import type { PublicUser } from '@/lib/types';

type Status = NonNullable<PublicUser['status']>;

const STATES: { id: Status; label: string; dot: string }[] = [
  { id: 'online', label: 'Online', dot: 'var(--good)' },
  { id: 'idle', label: 'Away', dot: 'var(--warn)' },
  { id: 'dnd', label: 'Do not disturb', dot: 'var(--bad)' },
  { id: 'invisible', label: 'Invisible', dot: 'var(--text-mute)' }
];

/** When the line under your name gives up on its own. */
/* Short enough that all five sit on one line of a sidebar. */
const CLEARS = [
  { minutes: 0, label: 'Never' },
  { minutes: 30, label: '30m' },
  { minutes: 60, label: '1h' },
  { minutes: 240, label: '4h' },
  { minutes: 8 * 60, label: 'Today' }
];

const MAX_TEXTS = 5;

/**
 * A section that grows to its own height and shrinks back.
 *
 * grid-template-rows from 0fr to 1fr is the only way to animate to a height
 * nobody measured, and it needs no ResizeObserver and no JavaScript on the
 * way through. The child holds overflow so the content is clipped while it
 * is shorter than itself rather than spilling out of the panel.
 */
function Expand({ open, children }: { open: boolean; children: React.ReactNode }) {
  return (
    <div
      className="grid transition-[grid-template-rows,opacity] duration-[260ms] ease-[var(--ease-out-soft,var(--ease-out))] motion-reduce:transition-none"
      style={{ gridTemplateRows: open ? '1fr' : '0fr', opacity: open ? 1 : 0 }}
      /* Folded to nothing is still in the page: without this a keyboard
         walks straight into four buttons nobody can see. */
      inert={!open}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}

export function StatusPanel({ me, open: shown, folded, onClose }: {
  me: PublicUser;
  /** Whether it is out. Kept mounted either way so it can animate shut. */
  open: boolean;
  /** True when the rail is icons-only and this has to stand beside it. */
  folded: boolean;
  onClose: () => void;
}) {
  const patchMe = useSession((s) => s.patchMe);
  const [text, setText] = useState(me.customStatus || '');
  /** Which half is expanded, if either. Only ever one at a time. */
  const [open, setOpen] = useState<'none' | 'states' | 'lines'>('none');
  const [kept, setKept] = useState<string[]>(me.statusTexts || []);
  /* Which of the five was chosen. The server only stores the moment the
     line lapses, and every not-never chip matched that equally - so
     picking 30 minutes lit 30 minutes, an hour, four hours and today. */
  const [clears, setClears] = useState<number>(() => (me.statusUntil && me.statusUntil > Date.now() ? -1 : 0));
  const box = useRef<HTMLDivElement>(null);

  const here = STATES.find((s) => s.id === me.status)
    ?? (me.status === 'offline' ? STATES[3]! : STATES[0]!);

  /** Saves the line on the way out, so nothing typed is ever lost. */
  const close = useRef(onClose);
  close.current = () => {
    const next = text.trim();
    if (next !== (me.customStatus || '')) void patchMe({ customStatus: next });
    onClose();
  };

  /*
   * Anywhere else puts it away.
   *
   * On the capture pass, so it closes even when the press lands on
   * something that stops the event on its way back up - and skipping the
   * row that opened it, whose own press is what closes it again.
   */
  useEffect(() => {
    const away = (e: PointerEvent) => {
      const el = box.current;
      const target = e.target as HTMLElement;
      if (el && !el.contains(target) && !target.closest('[data-status-toggle]')) close.current();
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') close.current(); };
    if (!shown) return;
    const t = window.setTimeout(() => document.addEventListener('pointerdown', away, true), 0);
    window.addEventListener('keydown', key);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('pointerdown', away, true);
      window.removeEventListener('keydown', key);
    };
  }, [shown]);

  /* Put away and reopened should not come back mid-errand with a half
     open list from last time. */
  useEffect(() => { if (!shown) setOpen('none'); }, [shown]);

  function pick(id: Status) {
    /* The store applies this to the copy in hand before it asks the
       server, so the dot moves on the press. */
    void patchMe({ status: id });
    setOpen('none');
  }

  function saveLines(next: string[]) {
    const clean = next.map((s) => s.trim()).filter(Boolean).slice(0, MAX_TEXTS);
    setKept(clean);
    void patchMe({ statusTexts: clean });
  }

  const rotate = !!me.statusRotate;

  /*
   * The body is the same in both rails. Only where it stands changes.
   *
   * A full rail has room for it, so it grows out of the line above your
   * name and pushes the rest of the sidebar up - the height animates from
   * nothing to whatever it needs, which is what makes it read as growing
   * rather than as a panel that was suddenly there.
   *
   * A folded rail is seventy-six pixels wide and this is not going to fit
   * in it, so it stands beside the rail instead, level with your face. It
   * is still the sidebar's, not a window: same surface, same edge, and the
   * same press puts it away.
   */
  const body = (
    <>

      {/* ---- the state you are in, which opens the others ---- */}
      <div className="p-1">
        <button
          type="button"
          aria-expanded={open === 'states'}
          onClick={() => setOpen((v) => (v === 'states' ? 'none' : 'states'))}
          className={cn(
            'flex w-full items-center gap-2.5 rounded-[calc(10px*var(--radius-mul,1))] border border-transparent px-2 py-1.5 text-left outline-none',
            'transition-[background-color,border-color] duration-[var(--t-hover)] ease-[var(--ease)]',
            'hover:bg-surface-3 focus-visible:shadow-[0_0_0_2px_var(--accent)]'
          )}
        >
          <span className="h-2.5 w-2.5 flex-none rounded-full" style={{ background: here.dot }} />
          <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-ink">{here.label}</span>
          <Icon
            name="chevronD"
            size={13}
            className={cn('flex-none text-mute transition-transform duration-[var(--t-hover)]', open === 'states' && 'rotate-180')}
          />
        </button>

        <Expand open={open === 'states'}>
          <div className="mt-1 border-t border-line-soft pt-1">
            {STATES.filter((s) => s.id !== here.id).map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => pick(s.id)}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-[calc(10px*var(--radius-mul,1))] border border-transparent px-2 py-1.5 text-left outline-none',
                  'transition-[background-color,border-color] duration-[var(--t-hover)] ease-[var(--ease)]',
                  'hover:bg-surface-3 focus-visible:shadow-[0_0_0_2px_var(--accent)]'
                )}
              >
                <span className="h-2.5 w-2.5 flex-none rounded-full" style={{ background: s.dot }} />
                <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-dim">{s.label}</span>
              </button>
            ))}
          </div>
        </Expand>
      </div>

      {/* ---- the line under your name, and what it does on its own ---- */}
      <div className="border-t border-line-soft p-2">
        <div className="flex items-center gap-1.5">
          <div className="min-w-0 flex-1">
            <EmojiField
              value={text}
              onChange={setText}
              placeholder="What are you up to?"
              maxLength={60}
              onKeyDown={(e) => { if (e.key === 'Enter') close.current(); }}
              className="!h-8 !text-[12.5px]"
            />
          </div>
          <button
            type="button"
            aria-label="What your line does on its own"
            aria-expanded={open === 'lines'}
            onClick={() => setOpen((v) => (v === 'lines' ? 'none' : 'lines'))}
            className={cn(
              'grid h-8 w-8 flex-none place-items-center rounded-[calc(8px*var(--radius-mul,1))] border border-transparent outline-none',
              'transition-[background-color,border-color,color] duration-[var(--t-hover)] ease-[var(--ease)]',
              open === 'lines' ? 'border-line bg-surface-3 text-ink' : 'text-mute hover:bg-surface-3 hover:text-ink',
              'focus-visible:shadow-[0_0_0_2px_var(--accent)]'
            )}
          >
            <Icon name="settings" size={14} />
          </button>
        </div>

        {/*
          What you are typing, as everybody else will see it.

          A status is the one field where you write :name: and cannot see what
          you wrote - the box is a plain input and a plain input draws text.
          This sits under it only while there is an icon to show.
        */}
        {/[a-z0-9_-]{2,32}:/i.test(text) ? (
          <p className="m-0 mt-1.5 flex min-w-0 items-center gap-1 px-0.5 text-[11.5px] text-mute">
            <span className="flex-none text-ghost">Reads as</span>
            <EmojiText text={text} size={13} className="min-w-0 truncate text-dim" />
          </p>
        ) : null}

        <Expand open={open === 'lines'}>
          <div className="mt-2 flex flex-col gap-2 border-t border-line-soft pt-2">
            {/* when it gives up on its own */}
            <div className="flex flex-wrap items-center gap-1">
              <span className="mr-0.5 text-[10.5px] font-semibold text-mute">Clears</span>
              {CLEARS.map((c) => (
                <button
                  key={c.minutes}
                  type="button"
                  aria-pressed={clears === c.minutes}
                  onClick={() => { setClears(c.minutes); void patchMe({ statusClearMinutes: c.minutes }); }}
                  className={cn(
                    'rounded-[calc(6px*var(--radius-mul,1))] border px-1.5 text-[10.5px] font-semibold leading-[18px] outline-none',
                    'transition-[background-color,border-color,color] duration-[var(--t-hover)] ease-[var(--ease)]',
                    'focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                    clears === c.minutes
                      ? 'border-accent bg-accent-soft text-accent'
                      : 'border-line-soft bg-surface-3 text-dim hover:border-line hover:text-ink'
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {/* the few you keep, as chips rather than a list */}
            <div className="flex flex-wrap items-center gap-1">
              <span className="mr-0.5 text-[10.5px] font-semibold text-mute">Saved {kept.length}/{MAX_TEXTS}</span>
              {kept.map((line, i) => (
                <span
                  key={i}
                  className="flex max-w-full items-center gap-0.5 rounded-[calc(6px*var(--radius-mul,1))] bg-surface-3 pr-0.5 pl-1.5"
                >
                  <button
                    type="button"
                    onClick={() => { setText(line); void patchMe({ customStatus: line }); }}
                    className="flex max-w-[92px] items-center gap-0.5 truncate text-[10.5px] leading-[18px] text-dim outline-none transition-colors hover:text-ink focus-visible:text-ink"
                  >
                    {/* As it will read, not as it was typed. */}
                    <EmojiText text={line} size={12} />
                  </button>
                  <button
                    type="button"
                    aria-label={'Forget ' + line}
                    onClick={() => saveLines(kept.filter((_, n) => n !== i))}
                    className="grid h-[18px] w-[14px] place-items-center text-ghost outline-none transition-colors hover:text-bad focus-visible:text-bad"
                  >
                    <Icon name="close" size={9} />
                  </button>
                </span>
              ))}
              {kept.length > 1 ? (
                <button
                  type="button"
                  aria-pressed={rotate}
                  title="Show a different saved line each day"
                  onClick={() => void patchMe({ statusRotate: !rotate })}
                  className={cn(
                    'rounded-[calc(6px*var(--radius-mul,1))] border px-1.5 text-[10.5px] font-semibold leading-[18px] outline-none',
                    'transition-[background-color,border-color,color] duration-[var(--t-hover)] ease-[var(--ease)]',
                    'focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                    rotate
                      ? 'border-accent bg-accent-soft text-accent'
                      : 'border-line-soft bg-surface-3 text-dim hover:border-line hover:text-ink'
                  )}
                >
                  Daily
                </button>
              ) : null}
              {kept.length < MAX_TEXTS && text.trim() && !kept.includes(text.trim()) ? (
                <button
                  type="button"
                  onClick={() => saveLines([...kept, text])}
                  className="rounded-[calc(6px*var(--radius-mul,1))] border border-dashed border-line px-1.5 text-[10.5px] font-semibold leading-[18px] text-mute outline-none transition-colors hover:border-accent-line hover:text-ink focus-visible:text-ink"
                >
                  + Keep
                </button>
              ) : null}
            </div>

          </div>
        </Expand>
      </div>

      {/*
        Your own profile, from here.

        Pressing your face used to open it, and that was replaced by this
        panel because the status is what people actually change. The profile
        is still worth one press, so it is one press - at the foot, where it
        does not compete with the thing you came for.
      */}
      <div className="border-t border-line-soft p-1">
        <button
          type="button"
          onClick={() => { onClose(); go('u/' + me.username); }}
          className={cn(
            'flex w-full items-center gap-2 rounded-[calc(10px*var(--radius-mul,1))] px-2 py-1.5 text-left outline-none',
            'transition-[background-color] duration-[var(--t-hover)] ease-[var(--ease)]',
            'hover:bg-surface-3 focus-visible:shadow-[0_0_0_2px_var(--accent)]'
          )}
        >
          <Icon name="profile" size={13} className="flex-none text-mute" />
          <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-dim">View my profile</span>
          <Icon name="chevronR" size={12} className="flex-none text-ghost" />
        </button>
      </div>
    </>
  );

  if (folded) {
    return (
      <div
        ref={box}
        role="group"
        aria-label="Your status"
        className={cn(
          'fixed bottom-3 left-[84px] z-[120] w-[262px] overflow-hidden rounded-[calc(12px*var(--radius-mul,1))]',
          'border border-line-soft bg-surface-2 shadow-[var(--shadow-3)]',
          'transition-[opacity,transform] duration-[260ms] ease-[var(--ease-out-soft,var(--ease-out))] motion-reduce:transition-none',
          shown ? 'translate-x-0 opacity-100' : 'pointer-events-none -translate-x-2 opacity-0'
        )}
        inert={!shown}
      >
        {body}
      </div>
    );
  }

  return (
    <div
      ref={box}
      role="group"
      aria-label="Your status"
      /* flex-none, no height cap and nothing to scroll: it belongs to the
         sidebar, so everything in it is small enough to fit there. Each half
         opens on its own and closes the other, which is what keeps the
         tallest state short. */
      className={cn(
        'mx-3 grid flex-none transition-[grid-template-rows,opacity,margin] duration-[260ms]',
        'ease-[var(--ease-out-soft,var(--ease-out))] motion-reduce:transition-none',
        shown ? 'my-2 opacity-100' : 'my-0 opacity-0'
      )}
      style={{ gridTemplateRows: shown ? '1fr' : '0fr' }}
      inert={!shown}
    >
      <div className="overflow-hidden rounded-[calc(12px*var(--radius-mul,1))] border border-line-soft bg-surface-2">
        {body}
      </div>
    </div>
  );
}
